// ============================================================================
// Shared currency + charge-model helpers (no React, no side effects).
// ----------------------------------------------------------------------------
// One source of truth for how money renders across the app. Mirrors the backend
// (viccofitness/db.py CURRENCY_SYMBOLS + compute_session_fee) so the symbol a
// trainer sees in the client editor, the session log and the business report is
// always the same and always derived the same way.
//
// The trainer bills in ONE currency (their account.default_currency), stored as
// a bare ISO code on trainer.default_currency / session.currency. The display
// symbol is a presentation concern resolved here. The API also returns a
// pre-resolved `currency_symbol` on some payloads (account, business report
// income); prefer that when present, fall back to this map otherwise.
//
// "Optional-first": a missing amount / unknown code yields "" or a clean number
// rather than a throw or a fabricated zero — matching the backend contract.
// ============================================================================

/** Currencies a trainer may bill in (matches db.SUPPORTED_CURRENCIES). */
export const SUPPORTED_CURRENCIES = ["HKD", "USD", "GBP", "EUR", "CNY"] as const;
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];

/**
 * ISO code -> display symbol. Matches the backend symbol convention
 * (viccofitness/db.py CURRENCY_SYMBOLS): HK$ / US$ / PS. Kept here so the
 * frontend can resolve a symbol from a code even on endpoints that don't ship
 * a pre-resolved `currency_symbol` (e.g. /auth/me).
 */
export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  HKD: "HK$",
  USD: "US$",
  GBP: "£",
  EUR: "€",
  CNY: "CN¥",
};

export function isSupportedCurrency(
  code: string | null | undefined,
): code is CurrencyCode {
  return code != null && (SUPPORTED_CURRENCIES as readonly string[]).includes(code);
}

/**
 * The display symbol/prefix for a currency code, e.g. "HK$" / "US$" / "£".
 * Returns "" when no currency is set, and falls back to the raw (trimmed) code
 * for any unknown value so we never show nothing for a real selection.
 */
export function currencySymbol(code: string | null | undefined): string {
  const c = (code ?? "").trim();
  if (!c) return "";
  return isSupportedCurrency(c) ? CURRENCY_SYMBOLS[c] : c;
}

/** Locale-aware number with the right number of decimals (0 for whole values). */
function fmtAmount(amount: number, locale?: string): string {
  const decimals = Number.isInteger(amount) ? 0 : 2;
  return amount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a money amount with the trainer's currency symbol, e.g. "HK$ 450",
 * "US$ 62.50", "PS 45". The symbol is resolved from the bare code via
 * {@link currencySymbol}; an explicit `symbol` (e.g. the API's pre-resolved
 * `currency_symbol`) takes precedence when supplied. null/undefined amount -> "".
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
  opts: { symbol?: string | null; locale?: string } = {},
): string {
  if (amount == null || !Number.isFinite(amount)) return "";
  const sym = (opts.symbol ?? currencySymbol(currency)).trim();
  const num = fmtAmount(amount, opts.locale);
  return sym ? `${sym} ${num}` : num;
}

// --- charge model -----------------------------------------------------------

/** The two charge models a client can be billed under (matches the backend). */
export type RateType = "per_session" | "per_hour";

export function isRateType(v: string | null | undefined): v is RateType {
  return v === "per_session" || v === "per_hour";
}

/**
 * Resolve the fee for a session from a client's charge model — a faithful port
 * of viccofitness/db.py::compute_session_fee so the SESSION form can show the
 * auto-filled fee live (before any round-trip) and match what the API will
 * store.
 *
 * Precedence (optional-first):
 *   - an `explicitFee` (the trainer typed an amount) always wins;
 *   - else a `per_session` rate yields `rateAmount` flat;
 *   - else a `per_hour` rate yields `rateAmount * (durationMin / 60)`, requiring
 *     a duration (null when duration is unknown);
 *   - else null (no charge model / insufficient inputs).
 */
export function computeSessionFee(
  rateType: string | null | undefined,
  rateAmount: number | null | undefined,
  explicitFee: number | null | undefined,
  durationMin: number | null | undefined,
): number | null {
  if (explicitFee != null) return explicitFee;
  if (rateAmount == null) return null;
  if (rateType === "per_session") return rateAmount;
  if (rateType === "per_hour") {
    if (durationMin == null) return null;
    return rateAmount * (durationMin / 60);
  }
  return null;
}

/** Round a computed fee to 2 dp, trimming binary float noise (e.g. 90.00001). */
export function roundFee(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
